import React from "react";
import { Add, CancelOutlined, CropFree, Delete, DeleteSweep, MyLocation } from "@mui/icons-material";
import { Alert, FormHelperText, IconButton, Stack, Tooltip, Typography } from "@mui/material";

import Swab from "ui/Swab/Swab";
import { Table, TableBody, TableCell, TableHead, TableRow } from "ui/Table";

import { getGcpColor } from "../types";
import { useImageOverlay } from "../useImageOverlay";

import { ActionIconButton, SidebarContainer } from "./GCPSidebar.styles";

const ACTION_ICON_SIZE = 16;

const formatCoord = (val: number | null, decimals = 6): string => {
  if (val == null) return "--";
  return val.toFixed(decimals);
};

const formatPixel = (val: number, hasMapPoint: boolean): string => {
  if (val === 0 && !hasMapPoint) return "--";
  return Math.round(val).toString();
};

const GCPSidebar: React.FC = () => {
  const { gcps, activeGcpId, mode, completeGcpCount, addGcp, removeGcp, startPlacingImage, startPlacingMap, cancelPlacing, clearAll } =
    useImageOverlay();

  return (
    <SidebarContainer>
      <Stack direction="row" justifyContent="space-between" alignItems="center" px={1} py={0.5}>
        <Typography variant="subtitle2">Ground Control Points</Typography>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Add GCP">
            <IconButton size="small" onClick={addGcp} disabled={mode !== "idle"}>
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear all">
            <IconButton size="small" onClick={clearAll}>
              <DeleteSweep fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {mode !== "idle" && (
        <Alert
          severity="info"
          sx={{ py: 0, mx: 1, mb: 0.5 }}
          action={
            <Tooltip title="Cancel">
              <IconButton size="small" onClick={cancelPlacing}>
                <CancelOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        >
          {mode === "place-image" ? "Click on the image to place point" : "Click on the map to place point"}
        </Alert>
      )}

      <Table variant="ag-grid-compact" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 36 }} />
            <TableCell sx={{ width: 50 }} align="right">
              X
            </TableCell>
            <TableCell sx={{ width: 50 }} align="right">
              Y
            </TableCell>
            <TableCell sx={{ width: 80 }} align="right">
              Lng
            </TableCell>
            <TableCell sx={{ width: 80 }} align="right">
              Lat
            </TableCell>
            <TableCell sx={{ width: 90 }} align="center">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {gcps.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="text.secondary" py={2}>
                  Click + to add a GCP
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {gcps.map((gcp) => {
            const isActive = gcp.id === activeGcpId;
            const hasImagePoint = gcp.imageX !== 0 || gcp.imageY !== 0 || gcp.mapLng != null;
            return (
              <TableRow key={gcp.id} selected={isActive} hover>
                <TableCell>
                  <Swab color={getGcpColor(gcp.colorIndex)} sx={{ width: 14, height: 14 }} />
                </TableCell>
                <TableCell align="right">{formatPixel(gcp.imageX, gcp.mapLng != null)}</TableCell>
                <TableCell align="right">{formatPixel(gcp.imageY, gcp.mapLng != null)}</TableCell>
                <TableCell align="right">{formatCoord(gcp.mapLng)}</TableCell>
                <TableCell align="right">{formatCoord(gcp.mapLat)}</TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0} justifyContent="center">
                    <Tooltip title="Set image point">
                      <span>
                        <ActionIconButton
                          size="small"
                          onClick={() => startPlacingImage(gcp.id)}
                          disabled={mode !== "idle"}
                          color={isActive && mode === "place-image" ? "primary" : "default"}
                        >
                          <CropFree sx={{ fontSize: ACTION_ICON_SIZE }} />
                        </ActionIconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Set map point">
                      <span>
                        <ActionIconButton
                          size="small"
                          onClick={() => startPlacingMap(gcp.id)}
                          disabled={mode !== "idle" || !hasImagePoint}
                          color={isActive && mode === "place-map" ? "primary" : "default"}
                        >
                          <MyLocation sx={{ fontSize: ACTION_ICON_SIZE }} />
                        </ActionIconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete GCP">
                      <ActionIconButton size="small" onClick={() => removeGcp(gcp.id)}>
                        <Delete sx={{ fontSize: ACTION_ICON_SIZE }} />
                      </ActionIconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {completeGcpCount < 3 && (
        <FormHelperText error sx={{ px: 1 }}>
          At least three valid GCPs are required
        </FormHelperText>
      )}
    </SidebarContainer>
  );
};

export default GCPSidebar;
